import _ from 'underscore';
import { useCallback, useEffect, useRef } from 'react';
import Promise from 'bluebird';

import { parseUrlSearchAndHashParams, UrlParameters } from './parse-url-search-and-hash-params';

export interface ActivityLogger {
  trace: (message: string) => void;
  error?: (error: unknown) => void;
}

export interface AuthorizationMessageEvent extends Pick<Event, 'isTrusted'> {
  data?: {
    source?: string;
    href?: string;
  };
}

export interface PopupParentWindow extends Pick<
  Window,
  'screenX' | 'screenY' | 'outerWidth' | 'outerHeight'
> {
  open: (url?: string | URL, target?: string, features?: string) => Pick<Window, 'closed'> | null;
  addEventListener: (type: 'message', listener: (event: AuthorizationMessageEvent) => void) => void;
  removeEventListener: (type: 'message', listener: (event: AuthorizationMessageEvent) => void) => void;
}

export interface OpenPopupAndWaitForMessageOptions {
  matcher: Partial<UrlParameters>;
  url: string;
  width?: number;
  height?: number;
}

const defaultActivityLogger: ActivityLogger = {
  trace: _.noop,
  error: _.noop,
};

const POPUP_WINDOW_WIDTH = 600;
const POPUP_WINDOW_HEIGHT = 680;
const POLLING_INTERVAL_IN_MS = 1000;

/**
 * Given a window object, poll until the window has been closed.
 * @param {Window} window - Window object.
 * @returns {Promise} - Resolves to true when window is closed.
 */
function pollForWindowClosed(window: Pick<Window, 'closed'> | null): Promise<boolean> {
  if (window == null || window.closed) {
    return Promise.resolve(true);
  }

  return Promise.delay(POLLING_INTERVAL_IN_MS).then(() => pollForWindowClosed(window));
}

/**
 * Provides a function to open a popup window and wait for it to return an authorization message.
 * @param {Window} windowObject - Parent window that will be used to open the popup and receive the message.
 * @param {Object} activity - Activity logger.
 * @returns {Function} - Function to open the window and wait for the message, returns a Promise.
 */
export function useOpenPopupAndWaitForMessage({
  windowObject = window,
  activity = defaultActivityLogger,
}: {
  windowObject?: PopupParentWindow;
  activity?: ActivityLogger;
} = {}) {
  // ref to an array of callbacks used to handle incoming messages
  // this array is modified by both the messageEventHandler() and openPopupAndWaitForMessage()
  const messageEventCallbacks = useRef<Array<(params: UrlParameters) => boolean>>([]);

  // handles incoming messages from the popup window
  const messageEventHandler = useCallback((event: AuthorizationMessageEvent) => {
    if (event.isTrusted && event.data?.source === 'AuthorizationComplete' && event.data.href) {
      const params = parseUrlSearchAndHashParams(event.data.href);

      // get the callbacks that are interested in this message
      const callbacksToRemove = _.filter(messageEventCallbacks.current, cb => cb(params));

      // remove any matched callbacks from the list
      messageEventCallbacks.current
        = _.difference(messageEventCallbacks.current, callbacksToRemove);
    }
  }, []);

  // listen for postMessage events from the popup window
  useEffect(() => {
    activity.trace('Started listening for postMessage events.');
    windowObject.addEventListener('message', messageEventHandler);

    return () => {
      activity.trace('Stopped listening for postMessage events.');
      windowObject.removeEventListener('message', messageEventHandler);
    };
  }, [windowObject, activity, messageEventHandler]);

  const getPopupWindowFeatures = useCallback((
    width = POPUP_WINDOW_WIDTH,
    height = POPUP_WINDOW_HEIGHT
  ) => {
    // calculate center point of parent window
    const centerX = windowObject.screenX + (windowObject.outerWidth / 2);
    const centerY = windowObject.screenY + (windowObject.outerHeight / 2);

    // set left and top so that popup is centered over the parent window
    const left = centerX - (width / 2);
    const top = centerY - (height / 2);

    const features = [
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
      'scrollbars=1',
      'toolbar=0',
    ];
    return features.join(',');
  }, [windowObject]);

  const openPopupAndWaitForMessage = useCallback(({
    matcher, url, width, height,
  }: OpenPopupAndWaitForMessageOptions): Promise<UrlParameters> => {
    activity.trace(`Opening pop-up window to ${url}...`);

    const popupWindowFeatures = getPopupWindowFeatures(width, height);
    const popupWindow = windowObject.open(url, '', popupWindowFeatures);

    // Returns a promise that will resolve with the matching authorization message. If the window is
    // closed before authorization completes, promise will reject with an error.
    return new Promise<UrlParameters>((resolve, reject) => {
      // add a callback to look for the matching message
      let message: UrlParameters | null = null;
      messageEventCallbacks.current.push((params) => {
        if (params && _.isMatch(params, matcher)) {
          activity.trace(`Found a matching AuthorizationComplete message. keys=[${_.keys(params).join(', ')}]`);
          message = params;
          resolve(message);
          return true;
        }

        activity.trace('Found a AuthorizationComplete message, it was not a match.');
        return false;
      });

      // reject promise if the authorization pop-up window is closed before getting the message
      pollForWindowClosed(popupWindow).then(() => {
        if (message) {
          // message has already been found and promise has been resolved, nothing else to do
          activity.trace('Pop-up window has been closed after message was received.');
        } else {
          activity.trace('Pop-up window was closed, message was never received.');
          reject(new Error('Pop-up window was closed, message was never received.'));
        }
      });
    });
  }, [windowObject, activity, getPopupWindowFeatures]);

  return openPopupAndWaitForMessage;
}
